import type { CogentSession } from "@softmax/cogent-sdk/react";
import {
  pageQuestion,
  type CogentLive,
  type CogentQuestion,
} from "../cogent/chat/chat-view";

/** Format native controller state for the corner widget and expanded conversation. */
export function widgetChatView(session: CogentSession): CogentLive {
  const { chat, confirmation } = session;
  const error = chat.control.error;
  const approval = chat.control.nativeApproval;
  const latest = chat.events.at(-1);
  const problem =
    error || (latest?.type === "turn.failed" ? latest.data.message : null);
  const result = (work: Promise<unknown>) =>
    work.then(
      () => true,
      (cause: unknown) => {
        chat.control.reportError(cause);
        return false;
      },
    );
  return {
    recordAssistantCommit: chat.control.recordAssistantCommit,
    messages: session.messages.map((message) => ({
      id: message.id,
      from: message.from,
      text: message.text,
      views: message.views,
      at: null,
      ...(message.queued ? { pending: "sending" as const } : {}),
    })),
    draft: session.draft,
    question: questionOf({ confirmation, approval }),
    status: {
      kind: error ? "offline" : chat.control.loaded ? "awake" : "starting",
      label:
        error ||
        (chat.control.loaded ? "Workspace connected" : "Opening workspace…"),
      canStart: Boolean(error),
    },
    doing: chat.control.active ? chat.control.activity || "Working…" : null,
    recovery: problem
      ? {
          title: "Conversation needs attention",
          detail: problem,
          attention: true,
        }
      : null,
    lessons: session.lessons,
    forgetLesson: session.forgetLesson,
    loaded: chat.control.loaded,
    canSend:
      chat.control.loaded && !["submitted", "resuming"].includes(chat.status),
    send: (message) => {
      if (message.answer?.questionId === pageQuestion) {
        confirmation?.answer(message.answer.choice === confirmation.yes);
        return Promise.resolve(true);
      }

      return result(
        message.answer
          ? chat.control.decideApproval(
              message.answer.choice === "Allow once" ? "once" : "deny",
            )
          : message.text.trim() === "/stop"
            ? chat.control.stop()
            : session.send(message.text),
      );
    },
    retry: () => {
      void result(chat.resume());
    },
    start: chat.control.connect,
  };
}

function questionOf({
  confirmation,
  approval,
}: Readonly<{
  confirmation: CogentSession["confirmation"];
  approval: CogentSession["chat"]["control"]["nativeApproval"];
}>): CogentQuestion | null {
  if (confirmation)
    return {
      id: pageQuestion,
      header: "Your call",
      question: confirmation.question,
      options: [
        { label: confirmation.yes, description: null },
        { label: "No", description: null },
      ],
      allowOther: false,
    };

  if (!approval) return null;

  return {
    id: approval.request_id,
    header: "Approval",
    question: approval.description ?? approval.command ?? "Allow this action?",
    options: [
      { label: "Allow once", description: null },
      { label: "Deny", description: null },
    ],
    allowOther: false,
  };
}
